import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle } from 'lucide-react'
import type { Technology } from '@/types/entities'

interface TechnologyDeleteDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    technology: Technology | null
    onConfirm: () => void
    isLoading?: boolean
}

export function TechnologyDeleteDialog({
    open,
    onOpenChange,
    technology,
    onConfirm,
    isLoading = false,
}: TechnologyDeleteDialogProps) {
    const { t } = useTranslation()
    if (!technology) return null

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>{t('technologies.deleteTechnologyTitle')}</DialogTitle>
                    <DialogDescription>
                        {/* Interpolated, not glued between fragments: word order differs by language. */}
                        {t('common.confirmDelete', { name: `${technology.manufacturer} ${technology.model}` })} {t('technologies.deleteWarning')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            {t('technologies.permanentAction')}
                        </AlertDescription>
                    </Alert>

                    <div className="bg-muted p-3 rounded-lg">
                        <div className="text-sm space-y-1">
                            <div><strong>{t('technologies.manufacturer')}:</strong> {technology.manufacturer}</div>
                            <div><strong>{t('technologies.category')}:</strong> {technology.category}</div>
                            <div><strong>{t('technologies.model')}:</strong> {technology.model}</div>
                            {technology.description && (
                                <div><strong>{t('technologies.description')}:</strong> {technology.description}</div>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? t('technologies.deleting') : t('technologies.deleteTechnology')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
